import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "Do I have to be in the Bay Area to attend this event?",
      answer:
        "Yes, this is an in-person event that requires physical attendance in Menlo Park, CA. We believe that the collaborative energy and networking opportunities that come from being together in person are essential to the Buildathon experience.",
    },
    {
      question: "Who can participate in the Buildathon?",
      answer: (
        <>
          The event is open to AI engineers, software engineers, and other
          technical professionals who are passionate about building innovative
          AI solutions. There is limited capacity for the event. Apply to join
          us{" "}
          <Link
            to="/apply"
            className="text-aifund hover:text-aifund-dark underline"
          >
            here
          </Link>
          .
        </>
      ),
    },
    {
      question: "What should I bring to the event?",
      answer:
        "Please bring your laptop, charger, and any specific tools or software you prefer to work with. We'll provide workspace, internet access, along with food and drinks throughout the event.",
    },
    {
      question: "Do I need to be on a team?",
      answer: (
        <>
          Participants may compete solo or in a team. Teams can be up to 4
          people. Whether you're working solo or with a team, you'll have access
          to the same resources and support throughout the event.
        </>
      ),
    },
    {
      question: "How do I find teammates?",
      answer:
        "Teams can be formed in several ways: you can apply with a pre-formed team, find teammates at the beginning of the event before the build time starts, or find teammates on the event's Discord server.",
    },
    {
      question:
        "When will I find out more about the projects I need to complete?",
      answer:
        "Complete project details and requirements will be shared via Discord at 10:30 AM on the day of the Buildathon.",
    },
    {
      question: "How will projects be judged?",
      answer: (
        <>
          <strong>Submission:</strong> A zip file of the source code, in as much polish as you have time for
          <br /><br />
          <strong>Format:</strong> A live deployed app with a live demonstration if qualified for finals. Zip file for validation.
          <br /><br />
          <strong>Platforms:</strong> Something that can be demo-ed via screenshare, so most people will build a web app
          <br /><br />
          <strong>Verification:</strong> Human review of source code to make sure it matches what is live demoed
          <br /><br />
          <strong>Scope:</strong> Yes, integrations are required. No mocks
          <br /><br />
          <strong>Judging:</strong> Feature completeness, quality, and polish
        </>
      ),
    },
    {
      question: "How do I get the Claude credits?",
      answer:
        "Detailed instructions for accessing your Claude credits will be sent to all registered participants via email prior to the event.",
    },

    {
      question: "What kind of support will be available during the event?",
      answer:
        "Product leaders and engineers from AI Fund and DeepLearning.AI’s ecosystem will be on hand to offer guidance and advice. Additionally, sponsor representatives will be available to answer questions about their products.",
    },
    {
      question: "Is there a livestream for those who can't attend?",
      answer:
        "Yes! We'll be livestreaming the whole day. We have exciting content planned, including a keynote address from Andrew Ng, a panel discussion on AI-assisted coding, and various interviews throughout the day.",
    },
    {
      question: "What happens after the Buildathon?",
      answer: (
        <>
          Interested in building more things? Join the AI Fund ecosystem. Here
          are{" "}
          <a
            href="https://careers.aifund.ai/jobs"
            target="_blank"
            rel="noopener noreferrer"
            className="text-aifund hover:text-aifund-dark underline"
          >
            open roles
          </a>{" "}
          at AI Fund and our portfolio companies.
        </>
      ),
    },
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="py-20 bg-white dark:bg-gray-900 transition-colors duration-300">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300">
            Everything you need to know about the Buildathon
          </p>
        </motion.div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden transition-colors duration-300"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
              >
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white pr-4">
                  {faq.question}
                </h3>
                <motion.div
                  animate={{ rotate: openIndex === index ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="flex-shrink-0"
                >
                  <svg
                    className="w-6 h-6 text-aifund"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </motion.div>
              </button>

              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-4">
                      <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* Contact Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
          className="text-center mt-16 bg-aifund/5 rounded-2xl p-8 border border-aifund/20"
        >
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Still Have Questions?
          </h3>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            Can't find what you're looking for? Our team is here to help!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/support-contact">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-aifund text-white px-6 py-3 rounded-full font-semibold hover:bg-aifund-dark transition-colors duration-200 inline-block"
              >
                Contact Support
              </motion.div>
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default FAQ;
